import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteAccountingSettings } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { Table } from "@/components/ui/Table";
import { ChartOfAccountsImportPanel } from "@/pages/stammdaten/ChartOfAccountsImportPanel";
import { AccountingSettingsPanel } from "@/pages/stammdaten/AccountingSettingsPanel";
import { CreateAccountModal } from "@/pages/stammdaten/CreateAccountModal";
import { EditAccountModal } from "@/pages/stammdaten/EditAccountModal";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import { formatTaxRatePercent } from "@/lib/articleConstants";
import type { Account } from "@/types/camping";

export function AccountsPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canWrite = canWriteAccountingSettings(groups);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Account | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<Account[]>("/api/v1/accounts?include_inactive=true")
      .then(setAccounts)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMeta({ title: "Stammdaten", subtitle: "Konten" });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-label-secondary">
        Erlöskonten aus dem Kontenrahmen SKR03 oder SKR04. Jedem Konto wird der
        Mehrwertsteuersatz zugeordnet. Artikel übernehmen diesen Satz von ihrem Buchungskonto;
        auf der Rechnung bleibt die Kontonummer als Snapshot gespeichert.
      </p>

      {canWrite ? <ChartOfAccountsImportPanel onImported={load} /> : null}

      <AccountingSettingsPanel canWrite={canWrite} />

      <EditAccountModal
        open={!!selected}
        account={selected}
        onClose={() => setSelected(null)}
        onSaved={load}
        canEdit={canWrite}
      />
      <CreateAccountModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />

      {!canWrite ? <StammdatenWriteHint /> : null}

      {canWrite ? (
        <div className="flex justify-end">
          <Button type="button" className="w-full sm:w-auto" onClick={() => setCreateOpen(true)}>
            Neues Konto
          </Button>
        </div>
      ) : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Laden…</p>
      ) : (
        <Table wide emptyMessage="Keine Konten." isEmpty={accounts.length === 0}>
          <thead>
            <tr>
              <th>Kontonummer</th>
              <th>Bezeichnung</th>
              <th>MwSt.</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <ClickableTableRow
                key={a.id}
                ariaLabel={`Konto ${a.account_number} öffnen`}
                onActivate={() => setSelected(a)}
              >
                <td className="font-medium tabular-nums">{a.account_number}</td>
                <td>{a.name}</td>
                <td className="tabular-nums">
                  {a.tax_rate_percent == null || a.tax_rate_percent === "" ? (
                    <span className="text-status-red">fehlt</span>
                  ) : (
                    `${formatTaxRatePercent(a.tax_rate_percent)} %`
                  )}
                </td>
                <td>{a.is_active ? "Aktiv" : "Inaktiv"}</td>
              </ClickableTableRow>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
